import * as Astronomy from 'astronomy-engine';

import { chironCovers, chironLongitude, chironRetrograde } from './chiron';
import { resolveMoment } from './time';
import { BODIES, BodyId, norm360, signIndexOf } from './zodiac';

export interface BirthPlace {
    name: string;
    /** Degrees north (negative south). */
    lat: number;
    /** Degrees east (negative west). */
    lon: number;
    /** IANA zone, e.g. "America/Sao_Paulo". */
    zone: string;
}

export interface BirthInput {
    year: number;
    month: number;
    day: number;
    /** Null when the birth time is not known: the chart then has no ascendant, angles or houses. */
    time: { hour: number; minute: number } | null;
    place: BirthPlace;
}

export type HouseSystem = 'placidus' | 'whole-sign';

export type ChartWarning =
    /** The clocks went back at that hour, so the local time happened twice; the first was used. */
    | 'ambiguous-time'
    /** The clocks went forward over that hour, so it never happened; read with the earlier offset. */
    | 'skipped-time'
    /** Placidus is undefined this far from the equator; whole-sign houses were used. */
    | 'polar-fallback'
    /** Without a birth time the Moon may have changed sign during the day. */
    | 'moon-sign-uncertain';

export interface PlacedBody {
    id: BodyId;
    /** Ecliptic longitude of date in degrees, 0 to 360. */
    lon: number;
    retrograde: boolean;
    /** 1 to 12; absent when the birth time is unknown. */
    house?: number;
}

export interface Angles {
    asc: number;
    mc: number;
    desc: number;
    ic: number;
}

export type PointId = BodyId | 'asc' | 'mc';

export interface Aspect {
    a: PointId;
    b: PointId;
    type: 'conjunction' | 'sextile' | 'square' | 'trine' | 'opposition';
    /** Distance from the exact angle in degrees. */
    orb: number;
}

export interface Chart {
    input: BirthInput;
    utcMs: number;
    offsetMinutes: number;
    bodies: PlacedBody[];
    angles: Angles | null;
    /** The twelve house cusps (longitudes), house 1 first; null without a birth time. */
    houses: number[] | null;
    houseSystem: HouseSystem | null;
    aspects: Aspect[];
    warnings: ChartWarning[];
}

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

const ASTRO_BODY: Record<Exclude<BodyId, 'node' | 'chiron'>, Astronomy.Body> = {
    sun: Astronomy.Body.Sun,
    moon: Astronomy.Body.Moon,
    mercury: Astronomy.Body.Mercury,
    venus: Astronomy.Body.Venus,
    mars: Astronomy.Body.Mars,
    jupiter: Astronomy.Body.Jupiter,
    saturn: Astronomy.Body.Saturn,
    uranus: Astronomy.Body.Uranus,
    neptune: Astronomy.Body.Neptune,
    pluto: Astronomy.Body.Pluto,
};

/** Julian centuries since J2000.0 (TT is close enough to UT for the slow terms used here). */
const centuries = (time: Astronomy.AstroTime): number => time.tt / 36525;

/** Apparent geocentric longitude on the true ecliptic of date, with light time and aberration. */
const bodyLongitude = (
    id: Exclude<BodyId, 'node' | 'chiron'>,
    time: Astronomy.AstroTime
): number =>
    Astronomy.Ecliptic(Astronomy.GeoVector(ASTRO_BODY[id], time, true)).elon;

const timeAt = (ms: number): Astronomy.AstroTime =>
    Astronomy.MakeTime(new Date(ms));

/**
 * The lunar north node: Meeus' mean node plus its five largest periodic terms (the "true" node
 * most astrology software shows; within a few arcminutes of it).
 */
export const northNodeLongitude = (time: Astronomy.AstroTime): number => {
    const T = centuries(time);
    const omega =
        125.0445479 -
        1934.1362891 * T +
        0.0020754 * T * T +
        (T * T * T) / 467441;
    const D = (297.8501921 + 445267.1114034 * T - 0.0018819 * T * T) * RAD;
    const M = (357.5291092 + 35999.0502909 * T) * RAD;
    const Mp = (134.9633964 + 477198.8675055 * T + 0.0087414 * T * T) * RAD;
    const F = (93.272095 + 483202.0175233 * T - 0.0036539 * T * T) * RAD;
    return norm360(
        omega -
            1.4979 * Math.sin(2 * (D - F)) -
            0.15 * Math.sin(M) -
            0.1226 * Math.sin(2 * D) +
            0.1176 * Math.sin(2 * F) -
            0.0801 * Math.sin(2 * (Mp - F))
    );
};

/** True obliquity of the ecliptic in degrees (mean obliquity plus the main nutation term). */
export const trueObliquity = (time: Astronomy.AstroTime): number => {
    const T = centuries(time);
    const mean =
        (84381.406 -
            46.836769 * T -
            0.0001831 * T * T +
            0.0020034 * T * T * T) /
        3600;
    const omega = (125.04452 - 1934.136261 * T) * RAD;
    const L = (280.4665 + 36000.7698 * T) * RAD;
    const nutation =
        (9.2025 * Math.cos(omega) + 0.5736 * Math.cos(2 * L)) / 3600;
    return mean + nutation;
};

/** The ecliptic longitude of the point of the ecliptic that has the given right ascension. */
const longitudeAtRa = (raDeg: number, eps: number): number =>
    norm360(
        Math.atan2(
            Math.sin(raDeg * RAD),
            Math.cos(raDeg * RAD) * Math.cos(eps * RAD)
        ) * DEG
    );

/** The ecliptic longitude on the eastern horizon. */
export const ascendantLongitude = (
    ramc: number,
    lat: number,
    eps: number
): number =>
    norm360(
        Math.atan2(
            Math.cos(ramc * RAD),
            -(
                Math.sin(ramc * RAD) * Math.cos(eps * RAD) +
                Math.tan(lat * RAD) * Math.sin(eps * RAD)
            )
        ) * DEG
    );

/** Ecliptic longitude of the medium coeli for a right ascension of the meridian. */
export const midheavenLongitude = (ramc: number, eps: number): number =>
    longitudeAtRa(ramc, eps);

/**
 * One intermediate Placidus cusp. `fraction` is how far along the semi-arc the cusp lies and `above`
 * is true for houses 11 and 12 (diurnal arc) and false for 2 and 3 (nocturnal arc). Returns null when
 * the point never rises or sets at this latitude.
 */
const placidusCusp = (
    ramc: number,
    lat: number,
    eps: number,
    fraction: number,
    above: boolean
): number | null => {
    const tanLat = Math.tan(lat * RAD);
    // Start from an equal-house guess and refine until the right ascension stops moving.
    let ra = above ? ramc + 90 * fraction : ramc + 180 - 90 * fraction;
    for (let i = 0; i < 100; i++) {
        const lon = longitudeAtRa(ra, eps);
        const dec = Math.asin(Math.sin(eps * RAD) * Math.sin(lon * RAD));
        const x = -tanLat * Math.tan(dec);
        if (Math.abs(x) >= 1) return null;
        const dsa = Math.acos(x) * DEG;
        const next = above
            ? ramc + fraction * dsa
            : ramc + 180 - fraction * (180 - dsa);
        const done = Math.abs(next - ra) < 1e-9;
        ra = next;
        if (done) break;
    }
    return longitudeAtRa(ra, eps);
};

/** The twelve Placidus cusps, or null where the system cannot be built (near the poles). */
export const placidusHouses = (
    ramc: number,
    lat: number,
    eps: number
): number[] | null => {
    const asc = ascendantLongitude(ramc, lat, eps);
    const mc = midheavenLongitude(ramc, eps);
    const c11 = placidusCusp(ramc, lat, eps, 1 / 3, true);
    const c12 = placidusCusp(ramc, lat, eps, 2 / 3, true);
    const c2 = placidusCusp(ramc, lat, eps, 2 / 3, false);
    const c3 = placidusCusp(ramc, lat, eps, 1 / 3, false);
    if (c11 === null || c12 === null || c2 === null || c3 === null) return null;
    return [
        asc,
        c2,
        c3,
        norm360(mc + 180),
        norm360(c11 + 180),
        norm360(c12 + 180),
        norm360(asc + 180),
        norm360(c2 + 180),
        norm360(c3 + 180),
        mc,
        c11,
        c12,
    ];
};

export const wholeSignHouses = (asc: number): number[] => {
    const start = signIndexOf(asc) * 30;
    return Array.from({ length: 12 }, (_, i) => norm360(start + i * 30));
};

/** The house (1 to 12) a longitude falls in, given the twelve cusps. */
export const houseOf = (lon: number, cusps: number[]): number => {
    const point = norm360(lon);
    for (let i = 0; i < 12; i++) {
        const span = norm360(cusps[(i + 1) % 12] - cusps[i]);
        if (norm360(point - cusps[i]) < span) return i + 1;
    }
    return 1;
};

const ASPECTS: { type: Aspect['type']; angle: number; orb: number }[] = [
    { type: 'conjunction', angle: 0, orb: 8 },
    { type: 'opposition', angle: 180, orb: 8 },
    { type: 'trine', angle: 120, orb: 7 },
    { type: 'square', angle: 90, orb: 7 },
    { type: 'sextile', angle: 60, orb: 5 },
];

const isAngle = (id: PointId) => id === 'asc' || id === 'mc';

const findAspects = (points: { id: PointId; lon: number }[]): Aspect[] => {
    const found: Aspect[] = [];
    for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
            const a = points[i];
            const b = points[j];
            // The two angles relate to each other by geometry alone; the pair says nothing.
            if (isAngle(a.id) && isAngle(b.id)) continue;
            const gap = Math.abs(norm360(a.lon - b.lon + 180) - 180);
            for (const asp of ASPECTS) {
                const orb = Math.abs(gap - asp.angle);
                if (orb <= asp.orb) {
                    found.push({ a: a.id, b: b.id, type: asp.type, orb });
                    break;
                }
            }
        }
    }
    return found.sort((x, y) => x.orb - y.orb);
};

const isRetrograde = (
    id: Exclude<BodyId, 'node' | 'chiron'>,
    utcMs: number
): boolean => {
    if (id === 'sun' || id === 'moon') return false;
    const before = bodyLongitude(id, timeAt(utcMs - 43_200_000));
    const after = bodyLongitude(id, timeAt(utcMs + 43_200_000));
    return norm360(after - before + 180) - 180 < 0;
};

export const computeChart = (
    input: BirthInput,
    houseSystem: HouseSystem = 'placidus'
): Chart => {
    const { place } = input;
    const known = input.time !== null;
    // Without a birth time the chart is cast for local noon.
    const moment = resolveMoment(
        {
            year: input.year,
            month: input.month,
            day: input.day,
            hour: input.time?.hour ?? 12,
            minute: input.time?.minute ?? 0,
        },
        place.zone
    );
    const warnings: ChartWarning[] = [];
    if (known && moment.ambiguous) warnings.push('ambiguous-time');
    if (known && moment.gap) warnings.push('skipped-time');

    const time = timeAt(moment.utcMs);
    const bodies: PlacedBody[] = BODIES
        // Chiron's table spans 1900 to 2050: outside it the chart simply has no Chiron.
        .filter((id) => id !== 'chiron' || chironCovers(moment.utcMs))
        .map((id) =>
            id === 'node'
                ? { id, lon: northNodeLongitude(time), retrograde: false }
                : id === 'chiron'
                  ? {
                        id,
                        lon: chironLongitude(moment.utcMs),
                        retrograde: chironRetrograde(moment.utcMs),
                    }
                  : {
                        id,
                        lon: norm360(bodyLongitude(id, time)),
                        retrograde: isRetrograde(id, moment.utcMs),
                    }
        );
    let angles: Angles | null = null;
    let houses: number[] | null = null;
    let used: HouseSystem | null = null;
    if (known) {
        const eps = trueObliquity(time);
        const gast = Astronomy.SiderealTime(time); // hours, at Greenwich
        const ramc = norm360(gast * 15 + place.lon);
        const asc = ascendantLongitude(ramc, place.lat, eps);
        const mc = midheavenLongitude(ramc, eps);
        angles = { asc, mc, desc: norm360(asc + 180), ic: norm360(mc + 180) };
        let cusps =
            houseSystem === 'placidus'
                ? placidusHouses(ramc, place.lat, eps)
                : null;
        used = 'placidus';
        if (cusps === null) {
            if (houseSystem === 'placidus') warnings.push('polar-fallback');
            cusps = wholeSignHouses(asc);
            used = 'whole-sign';
        }
        houses = cusps;
        for (const b of bodies) b.house = houseOf(b.lon, cusps);
    } else {
        // The Moon covers about 13 degrees a day: flag it if the day could change its sign.
        const start = resolveMoment(
            {
                year: input.year,
                month: input.month,
                day: input.day,
                hour: 0,
                minute: 0,
            },
            place.zone
        );
        const early = bodyLongitude('moon', timeAt(start.utcMs));
        const late = bodyLongitude('moon', timeAt(start.utcMs + 86_399_000));
        if (signIndexOf(early) !== signIndexOf(late))
            warnings.push('moon-sign-uncertain');
    }

    // The node and Chiron are points, not planets: their aspects are noise for a first reading.
    const points: { id: PointId; lon: number }[] = bodies
        .filter((b) => b.id !== 'node' && b.id !== 'chiron')
        .map((b) => ({ id: b.id, lon: b.lon }));
    if (angles)
        points.push(
            { id: 'asc', lon: angles.asc },
            { id: 'mc', lon: angles.mc }
        );

    return {
        input,
        utcMs: moment.utcMs,
        offsetMinutes: moment.offsetMinutes,
        bodies,
        angles,
        houses,
        houseSystem: used,
        aspects: findAspects(points),
        warnings,
    };
};
